// apps/web/src/features/crm/leads/activity/components/LeadFeedTabs.tsx
//
// Coarse category filter pills for the lead activity feed (#2315, reference
// `lead-parts.jsx` TimelineFeed). Supersedes the previous 5-option category
// `Select` — three broad buckets (all/notes/system) match the reference's
// tab row. Extracted out of `LeadEpisodeFeed` as its own presentational unit
// (a real responsibility boundary: pill-row rendering vs. feed data/filter
// composition) so the parent stays under the file-size soft limit. A plain
// button group with `aria-pressed` (NOT `role="tablist"`/`"tab"`) — this is
// a filter toggle, not a W3C tabpanel switch, matching the codebase's
// existing single-select pill convention (`EngagementBucketChips`).
import { useTranslation } from 'react-i18next';

export type LeadFeedTab = 'all' | 'notes' | 'system';

const TABS: readonly LeadFeedTab[] = ['all', 'notes', 'system'];

export interface LeadFeedTabsProps {
  value: LeadFeedTab;
  onChange: (tab: LeadFeedTab) => void;
}

export function LeadFeedTabs({ value, onChange }: LeadFeedTabsProps) {
  const { t } = useTranslation(['leads']);
  return (
    <div
      role="group"
      aria-label={t('leads:episode.filters.category')}
      data-testid="lead-feed-tabs"
      style={{ display: 'flex', gap: 6 }}
    >
      {TABS.map((tab) => {
        const active = tab === value;
        return (
          <button
            key={tab}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(tab)}
            data-testid={`lead-feed-tab-${tab}`}
            style={{
              height: 26,
              padding: '0 12px',
              borderRadius: 'var(--r-full)',
              fontSize: 12,
              border: `1px solid ${active ? 'transparent' : 'var(--border-1)'}`,
              background: active ? 'var(--accent-1-subtle)' : 'var(--bg-1)',
              color: active ? 'var(--accent-1-press)' : 'var(--fg-2)',
              fontWeight: active ? 500 : 400,
            }}
          >
            {t(`leads:episode.feedTabs.${tab}`)}
          </button>
        );
      })}
    </div>
  );
}
